import { Add, Close, Person } from "@mui/icons-material";
import { Box, Divider, IconButton, Modal, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Table, usePagination } from "@frontend/features/common/components/Table";
import { ModalBox } from "@frontend/features/common/utils/modals";
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useDebouncedState } from "@frontend/features/common/hooks/useDebouncedState";

import { useGetOrganizationsByAgency } from "../../queries";
import { CreateOrganizationModal } from "../CreateOrganizationModal";

export interface OrganizationsSelectorTableItem {
  name: string,
  id: string,
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (item: OrganizationsSelectorTableItem) => void;
  agencyId: number;
  selectedItemId?: string | number;
}

export const OrganizationsSelectorModal = ({ open, onClose, onSelect, agencyId, selectedItemId }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useDebouncedState('', 500);
  const [createModal, setCreateModal] = useState<boolean>(false);
  const pagination = usePagination();

  const toggleCreate = () => setCreateModal(prev => !prev);

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  const { data, isLoading } = useGetOrganizationsByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search,
    agencyId,
  });

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="organizations-modal-selector-title">
      <ModalBox display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={1}>
            <Person color="disabled" />
            <Typography id="organization-modal-selector-title" variant="h6">
            {t('organizations.selector.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider />

        <Box display="flex" alignItems="center" gap={3} mb={3}>
          <Typography
            variant="subtitle1"
            align="left"
            color={theme.palette.text.primary}
            sx={{
              flexGrow: 1,
            }}
          >
            {data?.count ?? 0} {t('organizations.results')}
          </Typography>
          <SearchInput placeholder={t('organizations.search')} onSearch={handleOnSearch} />

          <ToggleButtonGroup
            exclusive
            sx={{
              height: '40px',
              '& button': {
                padding: theme.spacing(1),
                width: '40px',
              },
            }}
          >
            <ToggleButton onClick={toggleCreate} value={t('organizations.selector.add')} aria-label={t('organizations.create.title')}>
              <Add />
            </ToggleButton>
          </ToggleButtonGroup>

        </Box>
        <CreateOrganizationModal agencyId={String(agencyId)} open={createModal} onClose={toggleCreate} />

        {isLoading && <Skeleton height="80px" variant="rounded" animation="wave" />}

        {(!isLoading && data) && <Table
          onRowSelect={onSelect}
          selectedItemId={selectedItemId}
          columns={[
            {
              id: 'name',
              label: t('organizations.table.name'),
              align: 'left',
            },
            {
              id: 'clientCount',
              label: t('organizations.table.memberCount'),
              align: 'left',
            },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              name: item.name,
              clientCount: item.clientCount
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />}

      </ModalBox>
    </Modal>
  );
};
